import React from "react";
import { createPortal } from "react-dom";
import type { Localizer } from "@/main/react/Types";
import { cn } from "@/lib/utils";
import { useMediaQueryMatches } from "@/main/react/hooks/useMediaQueryMatches";

const FOOTNOTE_PREVIEW_MEDIA_QUERY = "(hover: hover) and (pointer: fine)";
const FOOTNOTE_PREVIEW_MARGIN = 16;
const FOOTNOTE_PREVIEW_OFFSET = 14;

type Props = {
    contentRootRef: React.RefObject<HTMLElement | null>;
    localizer: Localizer;
};

type FootnoteReferenceElement = HTMLElement;

type ActiveFootnotePreview = {
    anchorEl: FootnoteReferenceElement;
    noteEl: HTMLElement;
    noteHtml: string;
    noteLabel?: string;
};

type FootnotePreviewPosition = {
    top: number;
    left: number;
    placement: "top" | "bottom";
};

function getClosestFootnoteReference(target: EventTarget | null): FootnoteReferenceElement | null {
    const element = target instanceof Element
        ? target
        : target instanceof Node
            ? target.parentElement
            : null;

    const closest = element?.closest?.(".footnote-ref");
    return closest instanceof HTMLElement ? closest : null;
}

function decodeHashTarget(hashValue: string): string {
    const rawValue = hashValue.startsWith("#") ? hashValue.slice(1) : hashValue;

    try {
        return decodeURIComponent(rawValue);
    } catch {
        return rawValue;
    }
}

function findFootnoteNoteElement(root: HTMLElement, referenceEl: FootnoteReferenceElement): HTMLElement | null {
    const controlledNoteId = referenceEl.getAttribute("data-footnote-note-id")?.trim()
        || referenceEl.getAttribute("aria-controls")?.trim();
    if (controlledNoteId) {
        return Array.from(root.querySelectorAll<HTMLElement>("[id]")).find((element) => element.id === controlledNoteId) ?? null;
    }

    const href = referenceEl.getAttribute("href")?.trim();
    if (!href?.startsWith("#")) {
        return null;
    }

    const noteId = decodeHashTarget(href);
    return Array.from(root.querySelectorAll<HTMLElement>("[id]")).find((element) => element.id === noteId) ?? null;
}

function buildFootnotePreviewHtml(noteEl: HTMLElement): string {
    const clone = noteEl.cloneNode(true) as HTMLElement;
    clone.removeAttribute("id");
    clone.querySelectorAll("a").forEach((link) => {
        const href = link.getAttribute("href")?.trim() ?? "";
        const text = link.textContent?.trim() ?? "";
        if (href.startsWith("#") && /^↩︎?$/.test(text)) {
            link.remove();
        }
    });

    const textContent = clone.textContent?.replace(/\s+/g, " ").trim() ?? "";
    return textContent ? clone.innerHTML.trim() : "";
}

/**
 * Can this reader hover a footnote marker at all? Gates the whole preview affordance.
 *
 * <p>Delegates to `useMediaQueryMatches` (2026-08-15). This was a `useState` mirror re-published from a
 * mount effect, which `AGENTS.md` rule 31 forbids. The delegate already carries every branch this body
 * had — the Safari < 14 `addListener` pair included — and answers `false` when `matchMedia` is
 * unavailable, which was this hook's initial state too. One behaviour difference, in the right
 * direction: the previous body reported "supported: false" for its first paint on EVERY browser,
 * because the state initializer ignored the query and only the mount effect consulted it. The delegate
 * answers correctly from first render, so a hover-capable reader no longer has a frame in which the
 * preview is disabled.
 */
function useFootnoteHoverPreviewSupport(): boolean {
    return useMediaQueryMatches(FOOTNOTE_PREVIEW_MEDIA_QUERY);
}

export default function DiscussionFootnotePreviews({
    contentRootRef,
    localizer,
}: Props) {
    const [activePreview, setActivePreview] = React.useState<ActiveFootnotePreview | null>(null);
    const [position, setPosition] = React.useState<FootnotePreviewPosition | null>(null);
    const hoverPreviewSupported = useFootnoteHoverPreviewSupport();
    const previewId = React.useId().replace(/:/g, "-");
    const popoverRef = React.useRef<HTMLDivElement | null>(null);
    const activePreviewRef = React.useRef<ActiveFootnotePreview | null>(null);

    React.useEffect(() => {
        activePreviewRef.current = activePreview;
    }, [activePreview]);

    const clearPreview = React.useCallback(() => {
        setActivePreview(null);
        setPosition(null);
    }, []);

    const showPreview = React.useCallback((referenceEl: FootnoteReferenceElement) => {
        const root = contentRootRef.current;
        if (!root || !root.contains(referenceEl)) {
            return;
        }

        const noteEl = findFootnoteNoteElement(root, referenceEl);
        if (!noteEl) {
            clearPreview();
            return;
        }

        const noteHtml = buildFootnotePreviewHtml(noteEl);
        if (!noteHtml) {
            clearPreview();
            return;
        }

        setActivePreview((previous) => {
            if (
                previous?.anchorEl === referenceEl
                && previous.noteEl === noteEl
                && previous.noteHtml === noteHtml
            ) {
                return previous;
            }

            return {
                anchorEl: referenceEl,
                noteEl,
                noteHtml,
                noteLabel: referenceEl.textContent?.trim() || undefined,
            };
        });
    }, [clearPreview, contentRootRef]);

    const togglePreview = React.useCallback((referenceEl: FootnoteReferenceElement) => {
        if (activePreviewRef.current?.anchorEl === referenceEl) {
            clearPreview();
            return;
        }

        showPreview(referenceEl);
    }, [clearPreview, showPreview]);

    React.useEffect(() => {
        const root = contentRootRef.current;
        if (!root) {
            return;
        }

        const handleMouseOver = (event: MouseEvent) => {
            if (!hoverPreviewSupported) {
                return;
            }

            const referenceEl = getClosestFootnoteReference(event.target);
            if (!referenceEl || !root.contains(referenceEl)) {
                return;
            }
            showPreview(referenceEl);
        };

        const handleMouseOut = (event: MouseEvent) => {
            if (!hoverPreviewSupported) {
                return;
            }

            const referenceEl = getClosestFootnoteReference(event.target);
            if (!referenceEl || activePreviewRef.current?.anchorEl !== referenceEl) {
                return;
            }
            clearPreview();
        };

        const handleFocusIn = (event: FocusEvent) => {
            const referenceEl = getClosestFootnoteReference(event.target);
            if (!referenceEl || !root.contains(referenceEl)) {
                return;
            }
            showPreview(referenceEl);
        };

        const handleFocusOut = (event: FocusEvent) => {
            const referenceEl = getClosestFootnoteReference(event.target);
            if (!referenceEl || activePreviewRef.current?.anchorEl !== referenceEl) {
                return;
            }
            clearPreview();
        };

        const handleClick = (event: MouseEvent) => {
            const referenceEl = getClosestFootnoteReference(event.target);
            if (!referenceEl || !root.contains(referenceEl)) {
                return;
            }

            event.preventDefault();
            togglePreview(referenceEl);
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== "Escape") {
                return;
            }

            const referenceEl = getClosestFootnoteReference(event.target);
            if (!referenceEl) {
                return;
            }

            clearPreview();
        };

        root.addEventListener("mouseover", handleMouseOver);
        root.addEventListener("mouseout", handleMouseOut);
        root.addEventListener("focusin", handleFocusIn);
        root.addEventListener("focusout", handleFocusOut);
        root.addEventListener("click", handleClick);
        root.addEventListener("keydown", handleKeyDown);

        return () => {
            root.removeEventListener("mouseover", handleMouseOver);
            root.removeEventListener("mouseout", handleMouseOut);
            root.removeEventListener("focusin", handleFocusIn);
            root.removeEventListener("focusout", handleFocusOut);
            root.removeEventListener("click", handleClick);
            root.removeEventListener("keydown", handleKeyDown);
        };
    }, [clearPreview, contentRootRef, hoverPreviewSupported, showPreview, togglePreview]);

    React.useEffect(() => {
        const root = contentRootRef.current;
        if (!root) {
            return;
        }

        root.querySelectorAll('[data-footnote-preview-open="true"]').forEach((element) => {
            element.removeAttribute("data-footnote-preview-open");
        });
        root.querySelectorAll(`[aria-describedby="${previewId}"]`).forEach((element) => {
            element.removeAttribute("aria-describedby");
        });

        if (!activePreview) {
            return;
        }

        activePreview.anchorEl.setAttribute("data-footnote-preview-open", "true");
        activePreview.anchorEl.setAttribute("aria-describedby", previewId);
        activePreview.anchorEl.setAttribute("aria-expanded", "true");
        activePreview.noteEl.setAttribute("data-footnote-preview-open", "true");

        return () => {
            activePreview.anchorEl.removeAttribute("data-footnote-preview-open");
            activePreview.anchorEl.removeAttribute("aria-describedby");
            activePreview.anchorEl.removeAttribute("aria-expanded");
            activePreview.noteEl.removeAttribute("data-footnote-preview-open");
        };
    }, [activePreview, contentRootRef, previewId]);

    React.useEffect(() => {
        if (!activePreview) {
            setPosition(null);
            return;
        }

        const updatePosition = () => {
            const referenceEl = activePreview.anchorEl;
            const popoverEl = popoverRef.current;
            if (!referenceEl.isConnected || !popoverEl) {
                clearPreview();
                return;
            }

            const referenceRect = referenceEl.getBoundingClientRect();
            const popoverRect = popoverEl.getBoundingClientRect();
            const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
            const popoverWidth = popoverRect.width || Math.min(360, Math.max(240, viewportWidth - (FOOTNOTE_PREVIEW_MARGIN * 2)));
            const popoverHeight = popoverRect.height || 0;
            const preferredTop = referenceRect.bottom + FOOTNOTE_PREVIEW_OFFSET;
            const preferredTopAbove = referenceRect.top - FOOTNOTE_PREVIEW_OFFSET - popoverHeight;
            const shouldPlaceAbove = popoverHeight > 0
                && preferredTop + popoverHeight > viewportHeight - FOOTNOTE_PREVIEW_MARGIN
                && preferredTopAbove >= FOOTNOTE_PREVIEW_MARGIN;
            const unclampedTop = shouldPlaceAbove ? preferredTopAbove : preferredTop;
            const maxTop = Math.max(FOOTNOTE_PREVIEW_MARGIN, viewportHeight - FOOTNOTE_PREVIEW_MARGIN - popoverHeight);
            const top = Math.min(Math.max(FOOTNOTE_PREVIEW_MARGIN, unclampedTop), maxTop);
            const centerX = referenceRect.left + (referenceRect.width / 2);
            const maxLeft = Math.max(FOOTNOTE_PREVIEW_MARGIN, viewportWidth - FOOTNOTE_PREVIEW_MARGIN - popoverWidth);
            const left = Math.min(
                Math.max(FOOTNOTE_PREVIEW_MARGIN, centerX - (popoverWidth / 2)),
                maxLeft,
            );

            const placement = shouldPlaceAbove ? "top" : "bottom";
            // Same position, same state: a scroll that did not move the anchor must not re-render.
            setPosition((current) => (
                current && current.top === top && current.left === left && current.placement === placement
                    ? current
                    : { top, left, placement }
            ));
        };

        // At most one layout read per frame. The scroll listener is capture-phase, so it fires for every
        // nested scroller too, often several times a frame (the `HeaderSearchSuggestions` pattern).
        let frame: number | null = null;
        const scheduleUpdate = () => {
            if (frame !== null) {
                return;
            }
            frame = window.requestAnimationFrame(() => {
                frame = null;
                updatePosition();
            });
        };

        updatePosition();
        window.addEventListener("resize", scheduleUpdate);
        window.addEventListener("scroll", scheduleUpdate, true);

        return () => {
            if (frame !== null) {
                window.cancelAnimationFrame(frame);
            }
            window.removeEventListener("resize", scheduleUpdate);
            window.removeEventListener("scroll", scheduleUpdate, true);
        };
    }, [activePreview, clearPreview]);

    if (!activePreview || typeof document === "undefined") {
        return null;
    }

    const previewTitle = activePreview.noteLabel
        ? localizer("discussion.footnote.preview.title", activePreview.noteLabel)
        : localizer("discussion.editor.toolbar.footnote");

    return createPortal(
        <div
            ref={popoverRef}
            id={previewId}
            role="tooltip"
            data-playwright-id="discussion-footnote-preview"
            className={cn(
                "pointer-events-none fixed z-[calc(var(--z-layer-overlay)-1)] max-w-[min(22rem,calc(100vw-2rem))] rounded-[1.25rem] border border-border/70 bg-[linear-gradient(180deg,color-mix(in_srgb,var(--card)_96%,white)_0%,var(--card)_100%)] px-4 py-3 text-sm text-foreground shadow-[var(--surface-shadow-md)] transition-opacity duration-150",
                position?.placement === "top" ? "origin-bottom" : "origin-top",
            )}
            style={{
                top: position?.top ?? -9999,
                left: position?.left ?? -9999,
                visibility: position ? "visible" : "hidden",
            }}
        >
            <div className="mb-2 text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                {previewTitle}
            </div>
            <div
                className="tiptap discussion-footnote-preview-content max-w-full text-sm leading-6"
                dangerouslySetInnerHTML={{ __html: activePreview.noteHtml }}
            />
        </div>,
        document.body,
    );
}
